import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useCompletePhoneVerification } from '@/api/gen/phone-verification/phone-verification.gen';
import { CheckSmallCircleFillIcon } from '@/icons';
import { LoadingSpinner } from '@/shared/components';
import { Input } from '@/ui';
import { Button } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { isCodeSentAtom, isEditingAtom, isVerifiedAtom, phoneNumberAtom, timerResetKeyAtom } from '../atoms/phoneVerificationAtoms';
import { useSendSmsCode, useVerifySmsCode } from '../hooks';
interface PhoneVerificationCodeInputProps {
  cooldownSeconds: number;
  isCooldownActive: boolean;
  onResendSuccess?: () => void;
}
export const PhoneVerificationCodeInput = ({
  cooldownSeconds,
  isCooldownActive,
  onResendSuccess
}: PhoneVerificationCodeInputProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const isProfileEditPage = router.pathname.includes('/profile/edit');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [showCooldownMessage, setShowCooldownMessage] = useState(false);
  const location = isProfileEditPage ? 'profile_edit' : 'phone_verification';
  const from = router.query.from as 'signup' | 'profile_edit' | 'banner_modal' || undefined;
  const trackEvent = (eventName: string) => {
    logger.track(eventName, {
      location,
      from
    });
  };
  const phoneNumber = useAtomValue(phoneNumberAtom);
  const setIsEditing = useSetAtom(isEditingAtom);
  const setIsCodeSent = useSetAtom(isCodeSentAtom);
  const setIsVerified = useSetAtom(isVerifiedAtom);
  const setTimerResetKey = useSetAtom(timerResetKeyAtom);
  const {
    mutate: completeVerification
  } = useCompletePhoneVerification({
    mutation: {
      onSuccess: () => {
        setIsEditing(false);
        setIsCodeSent(false);
        setIsVerified(false);
        toast.success(t('phoneVerify.newPhoneNumber.success'), {
          position: 'bottom-left',
          className: 'text-body-lg-semibold',
          icon: <CheckSmallCircleFillIcon size="md" color={ICON_COLOR.positive} />
        });
      }
    }
  });
  const {
    verifyCode,
    reset: resetVerifyError,
    isLoading: isVerifying,
    isSuccess: isVerified,
    isError,
    error
  } = useVerifySmsCode({
    onSuccess: () => {
      setIsVerified(true);
      if (isProfileEditPage) {
        completeVerification({
          data: {
            phoneNumber
          }
        });
      }
    },
    onError: error => {
      logger.captureError(error, {
        tags: {
          errorType: 'verify_sms_code_error'
        },
        extra: {
          location
        }
      });
    }
  });
  const {
    sendCode,
    error: sendError,
    isSuccess: sendSuccess,
    remainingAttempts
  } = useSendSmsCode({
    onSuccess: () => {
      onResendSuccess?.();
      setTimerResetKey(prev => prev + 1);
      setShowCooldownMessage(false);
    },
    onError: error => {
      logger.captureError(error, {
        tags: {
          errorType: 'send_sms_code_error'
        },
        extra: {
          location
        }
      });
    }
  });
  const handleResend = () => {
    trackEvent('auth_resend_click');
    if (isCooldownActive) {
      setShowCooldownMessage(true);
      return;
    }
    resetVerifyError();
    sendCode(phoneNumber);
  };

  // 쿨다운 종료 시 메시지 숨김
  useEffect(() => {
    if (!isCooldownActive) {
      setShowCooldownMessage(false);
    }
  }, [isCooldownActive]);

  // 에러 발생 시 인증번호 초기화
  useEffect(() => {
    if (isError) {
      setVerificationCode('');
    }
  }, [isError]);
  return <div className="flex w-full flex-col gap-xs" data-sentry-component="PhoneVerificationCodeInput" data-sentry-source-file="PhoneVerificationCodeInput.tsx">
      {/* 인증번호 입력 */}
      <div className="flex w-full items-center gap-xs">
        <div className="flex flex-1 flex-col gap-3xs">
          <div className="flex flex-col items-center gap-xs mobile-lg:flex-row">
            <Input type="text" inputMode="numeric" placeholder={t('phoneVerify.codeInput.placeholder')} value={verificationCode} onChange={e => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            setVerificationCode(value);
          }} readOnly={isVerifying || isVerified} className={cn('h-[46px] w-full border-none outline outline-[1px] outline-[#E9EAF2] focus-within:outline-strong focus-within:outline-[2px]', (isVerifying || isVerified) && 'pointer-events-none')} maxLength={6} autoFocus rightSlot={isVerified && <CheckSmallCircleFillIcon fill={ICON_COLOR.positive} />} data-sentry-element="Input" data-sentry-source-file="PhoneVerificationCodeInput.tsx" />

            {/* 인증 완료 전에만 버튼 표시 */}
            {!isVerified && <Button variant="contained" intent="strong" size="lg" onClick={() => {
            trackEvent('auth_complete_click');
            verifyCode(phoneNumber, verificationCode);
          }} disabled={verificationCode.length !== 6 || isVerifying} className="w-full min-w-[120px] shrink-0 mobile-lg:w-auto" leftSlot={isVerifying ? <LoadingSpinner className="size-4 text-white" /> : undefined}>
                {t('phoneVerify.codeInput.verify')}
              </Button>}
          </div>
          {/* 인증 에러 메시지 */}
          {!!error && <p className="pl-xs text-caption-md-medium text-destructive">
              {t(error)}
            </p>}
        </div>
      </div>

      {!isVerified && <>
          <div className="text-body-sm-medium text-neutral">
            {t('phoneVerify.codeInput.doesNotArrive')}{' '}
            <span className="cursor-pointer underline" onClick={handleResend}>
              {t('phoneVerify.codeInput.resend')}
            </span>
          </div>
          {!!sendError && <p className="text-body-sm-medium text-destructive">
              {t(sendError)}
            </p>}
          {showCooldownMessage && <p className="text-body-sm-medium text-destructive">
              {t('phoneVerify.codeInput.resendCooldown', {
          n: cooldownSeconds
        })}
            </p>}
          {sendSuccess && !showCooldownMessage && <p className="text-body-sm-medium text-destructive">
              {t('phoneVerify.codeInput.resendSuccess', {
          n: remainingAttempts
        })}
            </p>}
        </>}
    </div>;
};